import { useEffect, useMemo, useState } from "react";
import { ClipboardCheck, Play, Trash2, Plus, X, Loader2, CheckCircle2, Clock, ChevronRight, Copy, Pencil } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import ChecklistRunModal from "@/components/ChecklistRunModal";
import ChecklistEditorModal from "@/components/ChecklistEditorModal";

/**
 * ChecklistsView — inline vessel tab: shows system + custom templates plus
 * this vessel's run history. Starting a run opens ChecklistRunModal.
 *
 * Ships on web + native starting v1.5.0. Pro-gated at the Dashboard level
 * via <ProGate feature="checklists" .../>.
 */
export default function ChecklistsView({ vesselId, vesselName }) {
    const [templates, setTemplates] = useState(null);
    const [runs, setRuns] = useState(null);
    const [activeRun, setActiveRun] = useState(null);
    const [err, setErr] = useState("");
    // Custom-checklist editor state
    const [editorOpen, setEditorOpen] = useState(false);
    const [editorTemplate, setEditorTemplate] = useState(null);   // null = new blank, {} = edit
    const [editorInitialItems, setEditorInitialItems] = useState(null);  // "start from template" mode
    const [editorInitialName, setEditorInitialName] = useState(null);
    // "Start from template" picker (opened by New checklist ▸ From a template)
    const [startPickerOpen, setStartPickerOpen] = useState(false);

    const load = async () => {
        setErr("");
        try {
            const [t, r] = await Promise.all([
                api.get(`/checklists?vessel_id=${vesselId}`),
                api.get(`/checklist-runs?vessel_id=${vesselId}`),
            ]);
            setTemplates(t.data || []);
            setRuns(r.data || []);
        } catch (e) {
            setErr(formatApiErrorDetail(e));
        }
    };

    useEffect(() => { load(); }, [vesselId]);

    const startRun = async (tmpl) => {
        try {
            const { data } = await api.post(`/checklists/${tmpl.id}/start?vessel_id=${vesselId}`);
            setActiveRun(data);
            // Refresh runs so history includes the newly-started one immediately.
            load();
        } catch (e) {
            setErr(formatApiErrorDetail(e));
        }
    };

    const resumeRun = async (runId) => {
        try {
            const { data } = await api.get(`/checklist-runs/${runId}`);
            setActiveRun(data);
        } catch (e) {
            setErr(formatApiErrorDetail(e));
        }
    };

    const openBlankEditor = () => {
        setEditorTemplate(null);
        setEditorInitialItems(null);
        setEditorInitialName(null);
        setEditorOpen(true);
    };

    const openDuplicate = (tmpl) => {
        // Prefill items + suggested name, then open the editor
        setEditorTemplate(null);
        setEditorInitialItems((tmpl.items || []).map((it) => ({ label: it.label, note: it.note })));
        setEditorInitialName(`${tmpl.name} — my copy`);
        setEditorOpen(true);
        setStartPickerOpen(false);
    };

    const openEdit = (tmpl) => {
        setEditorTemplate(tmpl);
        setEditorInitialItems(null);
        setEditorInitialName(null);
        setEditorOpen(true);
    };

    const deleteTemplate = async (tmpl) => {
        if (!window.confirm(`Delete "${tmpl.name}"? This can't be undone (runs already completed keep their snapshot).`)) return;
        try {
            await api.delete(`/checklists/${tmpl.id}`);
            load();
        } catch (e) {
            setErr(formatApiErrorDetail(e));
        }
    };

    const closeModal = () => {
        setActiveRun(null);
        load();
    };

    if (templates === null) {
        return (
            <div style={{ padding: 40, textAlign: "center", color: "var(--bm-text-muted)" }}>
                <Loader2 size={22} className="bm-spin" />
            </div>
        );
    }

    const inProgress = (runs || []).filter((r) => r.status === "in_progress");
    const history = (runs || []).filter((r) => r.status === "complete").slice(0, 20);
    const builtIn = templates.filter((t) => t.is_system);
    const custom = templates.filter((t) => !t.is_system);

    return (
        <div data-testid="checklists-view" style={{ padding: "8px 0 40px" }}>
            {err && (
                <div data-testid="checklists-error" className="bm-card" style={{ padding: 12, marginBottom: 14, borderColor: "var(--bm-red, #d97a7a)", color: "var(--bm-red, #d97a7a)" }}>
                    {err}
                </div>
            )}

            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <ClipboardCheck size={18} color="var(--bm-gold)" />
                <h2 className="bm-serif" style={{ margin: 0, fontSize: 22, fontWeight: 500 }}>
                    Checklists
                </h2>
                <span style={{ color: "var(--bm-text-muted)", fontSize: 12, marginLeft: 4 }}>
                    for {vesselName}
                </span>
            </div>
            <p style={{ color: "var(--bm-text-muted)", fontSize: 13, margin: "0 0 20px", lineHeight: 1.5 }}>
                Winterize, spring commissioning, pre-departure. Track once, remember for years.
            </p>

            {/* In-progress runs — pinned at top so captains can resume */}
            {inProgress.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                    <SectionHeader label="In progress" testid="checklists-in-progress-header" />
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 10 }}>
                        {inProgress.map((r) => (
                            <RunCard
                                key={r.id}
                                run={r}
                                onClick={() => resumeRun(r.id)}
                                testid={`checklist-run-${r.id}`}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* Templates — built-in */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, gap: 10, flexWrap: "wrap" }}>
                <SectionHeader label="Built-in templates" testid="checklists-templates-header" />
                <button
                    data-testid="checklists-new-btn"
                    onClick={openBlankEditor}
                    className="bm-btn bm-btn-gold"
                    style={{ padding: "7px 14px", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 6 }}
                >
                    <Plus size={13} /> New checklist
                </button>
            </div>
            <div
                data-testid="checklists-templates-grid"
                style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12, marginBottom: 28 }}
            >
                {builtIn.map((t) => (
                    <TemplateCard
                        key={t.id}
                        tmpl={t}
                        onStart={() => startRun(t)}
                        onDuplicate={() => openDuplicate(t)}
                    />
                ))}
            </div>

            {/* Templates — captain's own custom */}
            {custom.length > 0 && (
                <>
                    <SectionHeader label={`Your custom checklists · ${custom.length}`} testid="checklists-custom-header" />
                    <div
                        data-testid="checklists-custom-grid"
                        style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12, marginBottom: 28 }}
                    >
                        {custom.map((t) => (
                            <TemplateCard
                                key={t.id}
                                tmpl={t}
                                onStart={() => startRun(t)}
                                onEdit={() => openEdit(t)}
                                onDelete={() => deleteTemplate(t)}
                            />
                        ))}
                    </div>
                </>
            )}

            {/* Completed history */}
            <SectionHeader label={`Completed history · ${history.length}`} testid="checklists-history-header" />
            {history.length === 0 ? (
                <div style={{ padding: "20px 16px", color: "var(--bm-text-muted)", fontSize: 13, textAlign: "center" }}>
                    No completed checklists yet. Start one above to build your history.
                </div>
            ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {history.map((r) => (
                        <HistoryRow key={r.id} run={r} onClick={() => resumeRun(r.id)} />
                    ))}
                </div>
            )}

            {activeRun && (
                <ChecklistRunModal
                    run={activeRun}
                    onClose={closeModal}
                    onChange={(fresh) => setActiveRun(fresh)}
                />
            )}

            <ChecklistEditorModal
                open={editorOpen}
                onClose={() => setEditorOpen(false)}
                onSaved={() => load()}
                vesselId={vesselId}
                vesselName={vesselName}
                template={editorTemplate}
                initialItems={editorInitialItems}
                initialName={editorInitialName}
            />
        </div>
    );
}

function SectionHeader({ label, testid }) {
    return (
        <div
            data-testid={testid}
            style={{
                fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase",
                color: "var(--bm-gold)", marginBottom: 10,
            }}
        >
            {label}
        </div>
    );
}

function TemplateCard({ tmpl, onStart, onDuplicate, onEdit, onDelete }) {
    const kindColors = {
        winterize: "#8fb4d4",
        dewinterize: "#a4d48f",
        startup: "var(--bm-gold)",
        custom: "var(--bm-text-muted)",
    };
    const badge = kindColors[tmpl.kind] || "var(--bm-text-muted)";
    return (
        <div
            data-testid={`checklist-template-${tmpl.id}`}
            className="bm-card"
            style={{ padding: "18px 18px 16px", display: "flex", flexDirection: "column", gap: 8, minHeight: 160 }}
        >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <span
                    style={{
                        fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase",
                        color: badge, borderBottom: `1px solid ${badge}`, paddingBottom: 2,
                    }}
                >
                    {tmpl.kind === "dewinterize" ? "Spring" : tmpl.kind}
                </span>
                {tmpl.is_system ? (
                    <span style={{ fontSize: 10, color: "var(--bm-text-dim)" }}>Built-in</span>
                ) : tmpl.vessel_id ? (
                    <span style={{ fontSize: 10, color: "var(--bm-text-dim)" }}>This vessel</span>
                ) : (
                    <span style={{ fontSize: 10, color: "var(--bm-text-dim)" }}>All my boats</span>
                )}
            </div>
            <h3 className="bm-serif" style={{ margin: "4px 0 0", fontSize: 18, fontWeight: 500 }}>{tmpl.name}</h3>
            <p style={{ margin: 0, color: "var(--bm-text-muted)", fontSize: 13, lineHeight: 1.5, flex: 1 }}>
                {tmpl.description || "No description."}
            </p>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 6, gap: 6, flexWrap: "wrap" }}>
                <div style={{ fontSize: 11, color: "var(--bm-text-dim)" }}>{tmpl.items.length} items</div>
                <div style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                    {onDuplicate && (
                        <button
                            type="button"
                            data-testid={`checklist-duplicate-${tmpl.id}`}
                            onClick={onDuplicate}
                            className="bm-btn bm-btn-ghost"
                            title="Duplicate & customize"
                            style={{ padding: "6px 10px", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 4 }}
                        >
                            <Copy size={12} /> Customize
                        </button>
                    )}
                    {onEdit && (
                        <button
                            type="button"
                            data-testid={`checklist-edit-${tmpl.id}`}
                            onClick={onEdit}
                            className="bm-btn bm-btn-ghost"
                            title="Edit"
                            style={{ padding: "6px 10px", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 4 }}
                        >
                            <Pencil size={12} /> Edit
                        </button>
                    )}
                    {onDelete && (
                        <button
                            type="button"
                            data-testid={`checklist-delete-${tmpl.id}`}
                            onClick={onDelete}
                            aria-label="Delete"
                            title="Delete"
                            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--bm-text-dim)", padding: 6, display: "flex" }}
                        >
                            <Trash2 size={13} />
                        </button>
                    )}
                    <button
                        data-testid={`checklist-start-${tmpl.id}`}
                        className="bm-btn bm-btn-gold"
                        onClick={onStart}
                        style={{ padding: "6px 14px", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 }}
                    >
                        <Play size={12} style={{ fill: "#1a1304" }} /> Start
                    </button>
                </div>
            </div>
        </div>
    );
}

function RunCard({ run, onClick, testid }) {
    return (
        <button
            type="button"
            data-testid={testid}
            className="bm-card"
            onClick={onClick}
            style={{
                textAlign: "left",
                padding: "14px 16px",
                display: "flex", flexDirection: "column", gap: 8,
                cursor: "pointer",
                background: "rgba(212,181,114,0.05)",
                border: "1px solid var(--bm-gold-dim)",
            }}
        >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--bm-text)" }}>
                    <Clock size={13} color="var(--bm-gold)" /> {run.template_name}
                </div>
                <ChevronRight size={14} color="var(--bm-text-muted)" />
            </div>
            <div style={{
                height: 6, borderRadius: 3, background: "rgba(255,255,255,0.06)", overflow: "hidden",
            }}>
                <div style={{
                    width: `${run.progress_pct || 0}%`, height: "100%",
                    background: "var(--bm-gold)", transition: "width 300ms",
                }} />
            </div>
            <div style={{ fontSize: 11, color: "var(--bm-text-muted)" }}>
                {run.progress_pct || 0}% — resume
            </div>
        </button>
    );
}

function HistoryRow({ run, onClick }) {
    const done = run.ticks.filter((t) => t.done).length;
    const total = run.ticks.length;
    const dateStr = (run.completed_at || run.started_at || "").slice(0, 10);
    return (
        <button
            type="button"
            data-testid={`checklist-history-${run.id}`}
            className="bm-card"
            onClick={onClick}
            style={{
                textAlign: "left",
                padding: "10px 14px",
                display: "flex", alignItems: "center", gap: 12,
                cursor: "pointer",
                background: "transparent",
            }}
        >
            <CheckCircle2 size={16} color="var(--bm-gold)" />
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: "var(--bm-text)" }}>
                    {run.template_name}
                </div>
                <div style={{ fontSize: 11, color: "var(--bm-text-muted)", marginTop: 2 }}>
                    {dateStr} · {done}/{total} items
                </div>
            </div>
            <ChevronRight size={14} color="var(--bm-text-muted)" />
        </button>
    );
}
